import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import Datatable from '../../../../components/datatable/datatable-xl.component';
import TruncatedName from '../../../../components/forms/truncated-name.component';
import Icon from '../../../../components/forms/icon.component';

interface Props {
  rows: any[];
  isLoading?: boolean;
  isRoot?: boolean;
  isAdmin?: boolean;
  onDownload?: (row: any) => void;
  onDelete?: (id: string | number) => Promise<void>;
  onEdit?: (row: any) => void;
}

function resolveLocale(language: string): string {
  const lang = (language || '').toLowerCase();
  if (lang.startsWith('zh')) return 'zh-CN';
  if (lang.startsWith('en')) return 'en-US';
  return 'es-MX';
}

function formatDate(iso: string, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

type TemplateTab = 'tone' | 'energy' | 'other';

const TEMPLATE_TABS: { key: TemplateTab; label: string }[] = [
  { key: 'tone', label: 'Tone Mode' },
  { key: 'energy', label: 'Energy Mode' },
  { key: 'other', label: 'Otros modos' }
];

const isEnergyRow = (r: any) => r.channel === 'energy' || r.config?.mode === 'energy';
const isToneRow = (r: any) =>
  r.channel === 'tone' || r.config?.mode === 'tone' || r.config?.mode === 'tone_detect';

export default function TableCPDTemplates({ rows, isLoading = false, onDelete, onEdit }: Props) {
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const { can } = usePermissions();
  const canWrite = can('cpd.write');

  const energyRows = useMemo(() => rows.filter(isEnergyRow), [rows]);
  const toneRows = useMemo(() => rows.filter(isToneRow), [rows]);
  // dtmf / ml / voice: sin este grupo, una plantilla de esos modos quedaba
  // invisible en cuanto hubiera plantillas energy y tone a la vez.
  const otherRows = useMemo(() => rows.filter((r) => !isEnergyRow(r) && !isToneRow(r)), [rows]);

  const groups: Record<TemplateTab, any[]> = {
    tone: toneRows,
    energy: energyRows,
    other: otherRows
  };
  const availableTabs = TEMPLATE_TABS.filter(({ key }) => groups[key].length > 0);
  const showTabs = availableTabs.length > 1;

  const [activeTab, setActiveTab] = useState<TemplateTab>('tone');

  useEffect(() => {
    if (!showTabs) return;
    if (groups[activeTab].length === 0) setActiveTab(availableTabs[0].key);
  }, [showTabs, activeTab, toneRows.length, energyRows.length, otherRows.length]);

  const displayedRows = showTabs ? groups[activeTab] : rows;

  const handleDeleteClick = async (r: any) => {
    const recordLabel = r.name ? `"${r.name}" (ID: #${r.id})` : `con ID: #${r.id}`;

    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      `Estás a punto de eliminar el siguiente registro: ${recordLabel}. ¿Verifica el registro antes de eliminarlo?`,
      () => Promise.resolve(true),
      'Continuar'
    );

    if (!isConfirmed) return;

    if (onDelete) {
      await onDelete(r.id);
    }
  };

  const columns = useMemo(() => {
    const textCell = (val: string | number, max = 100) => (
      <TruncatedName
        text={String(val)}
        maxWidth={max}
        className="fw-semibold fs-7 text-center w-100"
      />
    );

    return [
      {
        name: 'Nombre Config',
        sortable: true,
        minWidth: '180px',
        selector: (r: any) => r.name,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <TruncatedName text={r.name} maxWidth={200} className="fw-bolder fs-6 text-primary" />
            <TruncatedName
              text={r.description || 'Sin descripción'}
              maxWidth={200}
              className="fs-7 text-gray-700"
            />
          </div>
        )
      },
      {
        name: 'Carrier',
        sortable: true,
        minWidth: '100px',
        selector: (r: any) => r.toneCarrier || r.config?.toneCarrier,
        cell: (r: any) => {
          const carrier = r.toneCarrier || r.config?.toneCarrier;
          return <span className="badge badge-light-dark fw-bold">{carrier}</span>;
        }
      },
      {
        name: 'Acción',
        sortable: true,
        minWidth: '110px',
        selector: (r: any) => r.action || r.config?.action,
        cell: (r: any) => (
          <span className="text-danger fw-bold fs-7">{r.action || r.config?.action}</span>
        )
      },
      {
        name: 'Transferir A',
        sortable: true,
        minWidth: '130px',
        selector: (r: any) => r.config?.transferTo || r.transferTo,
        cell: (r: any) => {
          const transferNum = r.config?.transferTo || r.transferTo;
          return transferNum ? (
            <span className="fw-semibold fs-7 text-gray-800">{transferNum}</span>
          ) : (
            <span className="badge badge-light-secondary fw-semibold fs-8 px-2 py-1">
              Sin información
            </span>
          );
        }
      },
      {
        name: 'Max Analysis',
        sortable: true,
        center: true,
        minWidth: '110px',
        selector: (r: any) => r.maxAnalysisMs || r.config?.thresholds?.maxAnalysisMs,
        cell: (r: any) =>
          textCell(`${r.maxAnalysisMs || r.config?.thresholds?.maxAnalysisMs || 0} ms`)
      },
      {
        name: 'Min Energy',
        sortable: true,
        center: true,
        minWidth: '100px',
        selector: (r: any) => r.minEnergyDbfs || r.config?.thresholds?.minEnergyDbfs,
        cell: (r: any) =>
          textCell(`${r.minEnergyDbfs || r.config?.thresholds?.minEnergyDbfs || 0} dBfs`)
      },
      {
        name: 'Min Voice / Max Silence',
        sortable: false,
        center: true,
        minWidth: '160px',
        cell: (r: any) => {
          const th = r.config?.thresholds || r;
          return (
            <div className="fs-8 text-muted">
              V: <b>{th.minVoiceMs || 0}ms</b> | S: <b>{th.maxSilenceMs || 0}ms</b>
            </div>
          );
        }
      },
      {
        name: 'Tone Spec',
        sortable: true,
        minWidth: '220px',
        selector: (r: any) => r.toneSpec || r.config?.toneSpec,
        cell: (r: any) => (
          <TruncatedName
            text={r.toneSpec || r.config?.toneSpec || 'Sin descripción'}
            maxWidth={260}
            className="fs-7 text-gray-700"
          />
        )
      },
      {
        name: 'Creado',
        sortable: true,
        minWidth: '110px',
        selector: (r: any) => r.createdAt,
        cell: (r: any) => (
          <span className="fs-8 text-gray-600">{formatDate(r.createdAt, locale)}</span>
        )
      },
      {
        name: 'Acciones',
        center: true,
        minWidth: '160px',
        cell: (r: any) => {
          if (!canWrite) return <span className="text-muted fs-8">solo lectura</span>;
          return (
            <div className="d-flex align-items-center gap-2 position-relative">
              <button
                type="button"
                className="btn btn-icon btn-light-primary btn-color-primary btn-active-light-primary w-30px h-30px rounded-3"
                title="Editar"
                onClick={() => onEdit && onEdit(r)}
              >
                <Icon icon="ki-pencil" size="fs-2" />
              </button>
              {canWrite && (
                <button
                  type="button"
                  className="btn btn-icon btn-light-danger btn-color-danger btn-active-light-danger w-30px h-30px rounded-3"
                  title="Eliminar"
                  onClick={() => handleDeleteClick(r)}
                >
                  <Icon icon="ki-trash" size="fs-2" />
                </button>
              )}
            </div>
          );
        }
      }
    ];
  }, [locale, onEdit, canWrite]);

  return (
    <div>
      <AnimatePresence>
        {showTabs && (
          <motion.div
            className="card align-items-end shadow-sm border-0 mb-6"
            style={{ width: 'fit-content', marginLeft: 'auto' }}
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div className="badge badge-lg rounded-pill d-flex gap-1 align-items-center px-2 w-auto d-inline-flex">
              {availableTabs.map(({ key, label }) => {
                const isActive = activeTab === key;
                const count = groups[key].length;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setActiveTab(key)}
                    className="btn rounded-pill d-flex align-items-center gap-2 px-3 py-2 text-dark"
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="report-tab-pill"
                        className="bg-light-primary"
                        style={{ position: 'absolute', inset: 0, borderRadius: 50, zIndex: -1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span
                      className="fw-bolder fs-7"
                      style={{ color: isActive ? 'var(--bs-primary)' : '#3f4254' }}
                    >
                      {label}
                    </span>
                    <span
                      className={`badge rounded-pill fs-8 ${isActive ? 'badge-light-primary text-primary' : 'badge-light text-muted'}`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Datatable
        key={showTabs ? activeTab : 'single'}
        columns={columns}
        data={displayedRows}
        isLoading={isLoading}
      />
    </div>
  );
}
